import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
    ArrowLeft,
    Clock,
    AlertCircle,
    CheckCircle,
    User,
    Building,
    Mail,
    Phone,
    MessageSquare,
    Send,
    Package
} from 'lucide-react';
import ticketService from '../../../api/crm/support/ticketService';
import toast from 'react-hot-toast';
import { formatDate } from "../../../utils/stringUtils";

const TicketDetails = () => {
    const { ticketId } = useParams();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const [replyMessage, setReplyMessage] = useState('');

    // Fetch ticket details
    const { data: ticket, isLoading, error } = useQuery({
        queryKey: ['ticket', ticketId],
        queryFn: async () => {
            const result = await ticketService.getTicketById(ticketId);
            if (result.success) {
                return result.ticket;
            }
            throw new Error(result.error || 'Failed to load ticket details');
        },
    });

    // Update ticket mutation (for reply)
    const replyMutation = useMutation({
        mutationFn: async (updatedData) => {
            const result = await ticketService.updateTicket(ticketId, updatedData);
            if (result.success) {
                return result.ticket;
            }
            throw new Error(result.error || 'Failed to send reply');
        },
        onSuccess: () => {
            toast.success('Reply sent successfully');
            setReplyMessage('');
            queryClient.invalidateQueries(['ticket', ticketId]);
        },
        onError: (error) => {
            toast.error(error.message || 'Failed to send reply');
        }
    });

    const handleReply = (e) => {
        e.preventDefault();
        if (!replyMessage.trim()) return;

        // Construct new comment object
        const newComment = {
            agent: replyMessage.trim(),
            timestamp: new Date()
        };

        // Append to existing comments
        // Note: This relies on fetching the latest ticket first to avoid race conditions roughly, 
        // but ideally backend should handle push. Given current backend updateTicket replaces arrays:
        const currentComments = ticket.comment || [];
        const updatedComments = [...currentComments, newComment];

        replyMutation.mutate({
            comment: updatedComments,
            // Optionally update status if needed, e.g. back to 'in-progress' or 'pending'
            // status: 'pending' 
        });
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'open': return 'bg-blue-100 text-blue-800 border-blue-200';
            case 'in-progress': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
            case 'pending': return 'bg-orange-100 text-orange-800 border-orange-200';
            case 'resolved': return 'bg-green-100 text-green-800 border-green-200';
            case 'closed': return 'bg-gray-100 text-gray-800 border-gray-200';
            default: return 'bg-gray-100 text-gray-800 border-gray-200';
        }
    };

    if (isLoading) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0F2942]"></div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="p-8 text-center text-red-600 bg-red-50 rounded-lg m-4">
                <AlertCircle className="h-12 w-12 mx-auto mb-2" />
                <p>{error.message}</p>
                <button
                    onClick={() => navigate(-1)}
                    className="mt-4 px-4 py-2 text-sm font-medium text-red-600 bg-red-100 rounded-lg hover:bg-red-200"
                >
                    Go Back
                </button>
            </div>
        );
    }

    return (
        <div className="p-6 max-w-7xl mx-auto space-y-6">
            {/* Header / Nav */}
            <div className="flex items-center gap-4 mb-6">
                <button
                    onClick={() => navigate(-1)}
                    className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                >
                    <ArrowLeft className="h-6 w-6 text-gray-600" />
                </button>
                <div>
                    <h1 className="text-2xl font-bold text-gray-900">Ticket Details</h1>
                    <div className="text-sm text-gray-500 flex items-center gap-2">
                        <span>{ticket.ticketId}</span>
                        <span>•</span>
                        <span>Created {formatDate(ticket.createdAt)}</span>
                    </div>
                </div>
                <div className="ml-auto flex items-center gap-3">
                    <span className={`px-3 py-1 rounded-full text-sm font-medium border capitalize ${getStatusColor(ticket.status)}`}>
                        {ticket.status}
                    </span>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Main Content (Left Col) */}
                <div className="lg:col-span-2 space-y-6">
                    {/* Ticket Info Card */}
                    {/* Ticket Overview Card */}
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                        <h2 className="text-xl font-semibold text-gray-900 mb-4 break-all break-words capitalize">{ticket.title}</h2>

                        <div className="flex flex-wrap gap-4 pt-4 border-t border-gray-100">
                            {ticket.priority && (
                                <div className="flex items-center gap-2 text-sm">
                                    <AlertCircle className="h-4 w-4 text-gray-400" />
                                    <span className="text-gray-500">Priority:</span>
                                    <span className={`font-medium capitalize ${ticket.priority === 'high' ? 'text-red-600' :
                                        ticket.priority === 'medium' ? 'text-yellow-600' : 'text-green-600'
                                        }`}>
                                        {ticket.priority}
                                    </span>
                                </div>
                            )}
                            {ticket.category && (
                                <div className="flex items-center gap-2 text-sm">
                                    <MessageSquare className="h-4 w-4 text-gray-400" />
                                    <span className="text-gray-500">Category:</span>
                                    <span className="font-medium text-gray-900 capitalize">{ticket.category}</span>
                                </div>
                            )}
                            {ticket.orderId && (
                                <div className="flex items-center gap-2 text-sm">
                                    <Package className="h-4 w-4 text-gray-400" />
                                    <span className="text-gray-500">Order:</span>
                                    <span className="font-medium text-gray-900">
                                        {ticket.orderId?.orderNumber || ticket.orderId?.orderId || 'N/A'}
                                    </span>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Description Card */}
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                        <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4 border-b border-gray-100 pb-2">
                            Description
                        </h3>
                        <p className="text-gray-700 whitespace-pre-wrap break-words break-all overflow-wrap-anywhere leading-relaxed capitalize">
                            {ticket.description}
                        </p>
                    </div>

                    {/* Conversation */}
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                        <h3 className="text-lg font-semibold text-gray-900 mb-6 flex items-center gap-2">
                            <MessageSquare className="h-5 w-5 text-[#0F2942]" />
                            Conversation ({ticket.comment ? ticket.comment.length : 0})
                        </h3>

                        <div className="space-y-4 mb-8 max-h-[600px] overflow-y-auto pr-2 custom-scrollbar">
                            {(!ticket.comment || ticket.comment.length === 0) && (
                                <div className="text-center py-8 text-gray-500 italic bg-gray-50 rounded-lg">
                                    No messages yet. Start the conversation below.
                                </div>
                            )}

                            {ticket.comment && ticket.comment.map((item, idx) => {
                                const isAgent = !!item.agent;
                                const message = isAgent ? item.agent : item.customer;
                                const timestamp = item.timestamp || item.createdAt;


                                return (
                                    <div
                                        key={idx}
                                        className={`flex ${isAgent ? 'justify-end' : 'justify-start'}`}
                                    >
                                        <div className={`w-[90%] sm:w-[85%] bg-gray-50 p-4 shadow-sm ${isAgent
                                            ? 'border-r-4 border-agrosaf-green rounded-l-lg'
                                            : 'border-l-4 border-blue-500 rounded-r-lg'
                                            }`}>
                                            <div className="flex items-center justify-between mb-2">
                                                <div className="flex items-center gap-2">
                                                    <div className={`w-8 h-8 rounded-full flex items-center justify-center ${isAgent ? 'bg-agrosaf-green' : 'bg-blue-500'
                                                        }`}>
                                                        <span className="text-white text-sm font-medium">
                                                            {isAgent ? 'S' : 'C'}
                                                        </span>
                                                    </div>
                                                    <div>
                                                        <p className="text-sm font-medium text-gray-900 capitalize">
                                                            {isAgent ? 'Support Team' : (ticket.customerName || 'Customer')}
                                                        </p>
                                                    </div>
                                                </div>
                                                <span className="text-xs text-gray-500">
                                                    {formatDate(timestamp)}
                                                </span>
                                            </div>
                                            <p className="text-gray-700 text-sm whitespace-pre-wrap break-words break-all overflow-wrap-anywhere leading-relaxed">
                                                {message}
                                            </p>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>

                    </div>

                    {/* Reply Section */}
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                            <Send className="h-5 w-5 text-[#0F2942]" />
                            Internal Reply
                        </h3>
                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Your Message
                                </label>
                                <textarea
                                    value={replyMessage}
                                    onChange={(e) => setReplyMessage(e.target.value)}
                                    placeholder="Type your internal reply..."
                                    rows={4}
                                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0F2942]/50 focus:border-transparent outline-none transition-all resize-none"
                                />
                            </div>
                            <button
                                onClick={handleReply}
                                disabled={replyMutation.isPending || !replyMessage.trim()}
                                className="w-full bg-gradient-to-r from-blue-600 to-purple-600 text-white py-3 px-6 rounded-lg font-medium hover:from-blue-700 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-[#0F2942]/50 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2"
                            >
                                {replyMutation.isPending ? 'Sending...' : (
                                    <>
                                        <Send className="h-4 w-4" />
                                        Send Reply
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>

                {/* Sidebar (Right Col) */}
                <div className="space-y-6">
                    {/* Customer Profil Card */}
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4 border-b pb-2">Customer Info</h3>
                        <div className="space-y-4">
                            <div className="flex items-start gap-3">
                                <User className="h-5 w-5 text-gray-400 mt-0.5" />
                                <div>
                                    <p className="text-sm font-medium text-gray-900 capitalize">{ticket.customerName}</p>
                                    <p className="text-xs text-gray-500">Contact Person</p>
                                </div>
                            </div>
                            {ticket.customerId?.email && (
                                <div className="flex items-start gap-3">
                                    <Mail className="h-5 w-5 text-gray-400 mt-0.5" />
                                    <div className="overflow-hidden">
                                        <p className="text-sm font-medium text-gray-900 break-words">{ticket.customerId.email}</p>
                                        <p className="text-xs text-gray-500">Email</p>
                                    </div>
                                </div>
                            )}
                            {ticket.customerId?.phone && (
                                <div className="flex items-start gap-3">
                                    <Phone className="h-5 w-5 text-gray-400 mt-0.5" />
                                    <div>
                                        <p className="text-sm font-medium text-gray-900">{ticket.customerId.phone}</p>
                                        <p className="text-xs text-gray-500">Phone</p>
                                    </div>
                                </div>
                            )}
                            {ticket.businessName && (
                                <div className="flex items-start gap-3">
                                    <Building className="h-5 w-5 text-gray-400 mt-0.5" />
                                    <div>
                                        <p className="text-sm font-medium text-gray-900 break-all break-words">{ticket.businessName}</p>
                                        <p className="text-xs text-gray-500">Business</p>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Metadata Card */}
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4 border-b pb-2">Ticket Info</h3>
                        <div className="space-y-3 text-sm">
                            <div className="flex justify-between">
                                <span className="text-gray-500">Source</span>
                                <span className="font-medium capitalize">{ticket.source || 'N/A'}</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-gray-500">Assignee</span>
                                <span className="font-medium">{ticket.assignee ? `${ticket.assignee.firstName} ${ticket.assignee.lastName}` : 'Unassigned'}</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-gray-500">Last Updated</span>
                                <span className="font-medium">{formatDate(ticket.updatedAt)}</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default TicketDetails;
